import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@vehix/portal/components/ui/card';
import { Button } from '@vehix/portal/components/ui/button';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Switch } from '@vehix/portal/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@vehix/portal/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@vehix/portal/components/ui/dropdown-menu';
import {
  Play,
  MoreVertical,
  Edit,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Zap,
  History,
  ChevronDown,
  ChevronUp,
  Globe,
  Hash
} from 'lucide-react';
import {
  Schedule,
  ScheduleWithRunningStatus,
  describeCron,
  TIMEZONE_OPTIONS
} from '@vehix/portal/services/api/scheduling';
import { cn } from '@vehix/portal/lib/utils';
import {
  normalizeStatus,
  isCompletedStatus,
  isFailedStatus,
  isRunningStatus
} from '@vehix/portal/utils/executionStatus';
import { parseScheduleName } from '@vehix/portal/utils/scheduleNameParser';

interface CompactScheduleCardProps {
  schedule: ScheduleWithRunningStatus & { _fromHistory?: boolean };
  onToggleEnabled: (schedule: Schedule) => void;
  onTrigger: (schedule: Schedule) => void;
  onEdit: (schedule: Schedule) => void;
  onDelete: (schedule: Schedule) => void;
  onViewHistory: (schedule: Schedule) => void;
  isToggling?: boolean;
  isTriggering?: boolean;
}

export const CompactScheduleCard: React.FC<CompactScheduleCardProps> = ({
  schedule,
  onToggleEnabled,
  onTrigger,
  onEdit,
  onDelete,
  onViewHistory,
  isToggling,
  isTriggering
}) => {
  // Auto-expand running schedules
  const [isExpanded, setIsExpanded] = useState(schedule.is_running);

  // Update expansion state when running status changes
  useEffect(() => {
    if (schedule.is_running) {
      setIsExpanded(true);
    }
  }, [schedule.is_running]);

  // Format relative time (e.g., "2h ago", "just now")
  const formatRelativeTime = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get status indicator for last run
  const getStatusIndicator = () => {
    if (schedule.is_running) {
      return (
        <span className='flex items-center gap-1 text-blue-600 dark:text-blue-400'>
          <Loader2 className='h-3 w-3 animate-spin' />
          <span className='text-xs'>Running</span>
        </span>
      );
    }

    const status = schedule.last_run_status;
    if (!status) {
      return <span className='text-xs text-muted-foreground'>No runs yet</span>;
    }

    const normalized = normalizeStatus(status);

    if (isCompletedStatus(normalized)) {
      return (
        <span className='flex items-center gap-1 text-green-600 dark:text-green-400'>
          <CheckCircle className='h-3 w-3' />
          <span className='text-xs'>
            Success · {formatRelativeTime(schedule.last_triggered_at)}
          </span>
        </span>
      );
    }

    if (isFailedStatus(normalized)) {
      return (
        <span className='flex items-center gap-1 text-red-600 dark:text-red-400'>
          <XCircle className='h-3 w-3' />
          <span className='text-xs'>Failed · {formatRelativeTime(schedule.last_triggered_at)}</span>
        </span>
      );
    }

    if (isRunningStatus(normalized)) {
      return (
        <span className='flex items-center gap-1 text-blue-600 dark:text-blue-400'>
          <Loader2 className='h-3 w-3 animate-spin' />
          <span className='text-xs'>Running</span>
        </span>
      );
    }

    return (
      <span className='text-xs text-muted-foreground'>
        {status} · {formatRelativeTime(schedule.last_triggered_at)}
      </span>
    );
  };

  // Get timezone display label
  const getTimezoneLabel = (tz: string) => {
    const option = TIMEZONE_OPTIONS.find(o => o.value === tz);
    return option?.label || tz;
  };

  return (
    <Card
      className={cn(
        'transition-all duration-200',
        !schedule.is_enabled && 'opacity-60',
        schedule.is_running && 'ring-2 ring-blue-400 dark:ring-blue-600 shadow-md'
      )}
    >
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CardContent className='p-4'>
          {/* Header Row - Always Visible */}
          <div className='flex items-start justify-between gap-3'>
            {/* Left: Core Info */}
            <div className='flex-1 min-w-0'>
              {/* Schedule Name + Status Dot */}
              <div className='flex items-center gap-2'>
                {schedule.is_running ? (
                  <Loader2 className='h-3 w-3 text-blue-500 animate-spin flex-shrink-0' />
                ) : (
                  <div
                    className={cn(
                      'w-2 h-2 rounded-full flex-shrink-0',
                      schedule.is_enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                    )}
                  />
                )}
                <h4 className='font-medium truncate' title={schedule.schedule_name}>
                  {parseScheduleName(schedule.schedule_name)}
                </h4>
              </div>

              {/* Frequency */}
              <p className='text-sm text-muted-foreground mt-1 flex items-center gap-1'>
                <Clock className='h-3 w-3' />
                {describeCron(schedule.cron_expression)}
              </p>

              {/* Last Run Status */}
              <div className='mt-2'>{getStatusIndicator()}</div>
            </div>

            {/* Right: Actions */}
            <div className='flex items-center gap-2 flex-shrink-0'>
              <Switch
                checked={schedule.is_enabled}
                onCheckedChange={() => onToggleEnabled(schedule)}
                disabled={isToggling}
                aria-label={schedule.is_enabled ? 'Disable schedule' : 'Enable schedule'}
              />

              <Button
                variant='outline'
                size='sm'
                onClick={() => onTrigger(schedule)}
                disabled={isTriggering || !schedule.is_enabled || schedule.is_running}
                title={
                  schedule.is_running
                    ? 'Schedule is already running'
                    : !schedule.is_enabled
                      ? 'Enable schedule to trigger'
                      : 'Trigger now'
                }
                className='h-8 w-8 p-0'
              >
                {isTriggering || schedule.is_running ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <Play className='h-4 w-4' />
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                    <MoreVertical className='h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem onClick={() => onEdit(schedule)}>
                    <Edit className='h-4 w-4 mr-2' />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onViewHistory(schedule)}>
                    <History className='h-4 w-4 mr-2' />
                    View History
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className='text-destructive' onClick={() => onDelete(schedule)}>
                    <Trash2 className='h-4 w-4 mr-2' />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Expand/Collapse Toggle */}
              <CollapsibleTrigger asChild>
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-8 w-8 p-0'
                  aria-label={isExpanded ? 'Show less' : 'Show more'}
                >
                  {isExpanded ? (
                    <ChevronUp className='h-4 w-4' />
                  ) : (
                    <ChevronDown className='h-4 w-4' />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>

          {/* Expanded Content */}
          <CollapsibleContent>
            <div className='mt-4 pt-4 border-t'>
              {/* 2-Column Grid Layout */}
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2'>
                {/* Left Column */}
                <div className='space-y-2'>
                  {/* Task Name */}
                  <div className='text-sm'>
                    <span className='text-muted-foreground text-xs uppercase tracking-wide'>
                      Task
                    </span>
                    <p className='mt-0.5'>
                      <code className='text-xs bg-muted px-2 py-0.5 rounded font-mono'>
                        {schedule.task_name}
                      </code>
                    </p>
                  </div>

                  {/* Timezone */}
                  <div className='text-sm'>
                    <span className='text-muted-foreground text-xs uppercase tracking-wide'>
                      Timezone
                    </span>
                    <p className='mt-0.5 flex items-center gap-1'>
                      <Globe className='h-3 w-3 text-muted-foreground' />
                      {getTimezoneLabel(schedule.time_zone)}
                    </p>
                  </div>
                </div>

                {/* Right Column */}
                <div className='space-y-2'>
                  {/* Run Count */}
                  <div className='text-sm'>
                    <span className='text-muted-foreground text-xs uppercase tracking-wide'>
                      Total Runs
                    </span>
                    <p className='mt-0.5 flex items-center gap-1'>
                      <Hash className='h-3 w-3 text-muted-foreground' />
                      {schedule.run_count} {schedule.run_count === 1 ? 'run' : 'runs'}
                    </p>
                  </div>

                  {/* Auto VIN Processing */}
                  <div className='text-sm'>
                    <span className='text-muted-foreground text-xs uppercase tracking-wide'>
                      Automation
                    </span>
                    <p className='mt-0.5'>
                      {schedule.auto_trigger_vin_processing ? (
                        <Badge variant='secondary' className='text-xs'>
                          <Zap className='h-3 w-3 mr-1' />
                          Auto VIN
                          {schedule.vin_processing_config?.oem && (
                            <span className='ml-1 opacity-70'>
                              ({schedule.vin_processing_config.oem})
                            </span>
                          )}
                        </Badge>
                      ) : (
                        <span className='text-muted-foreground/60'>None</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Description - Full Width */}
              {schedule.description && (
                <div className='mt-3 text-sm'>
                  <span className='text-muted-foreground text-xs uppercase tracking-wide'>
                    Description
                  </span>
                  <p className='mt-0.5 text-muted-foreground/80 line-clamp-2'>
                    {schedule.description}
                  </p>
                </div>
              )}

              {/* Original Schedule Name - Full Width (only if different from display name) */}
              {parseScheduleName(schedule.schedule_name) !== schedule.schedule_name && (
                <div className='mt-3 text-sm'>
                  <span className='text-muted-foreground text-xs uppercase tracking-wide'>
                    Schedule ID
                  </span>
                  <p className='mt-0.5'>
                    <code className='text-xs bg-muted px-2 py-0.5 rounded font-mono text-muted-foreground'>
                      {schedule.schedule_name}
                    </code>
                  </p>
                </div>
              )}

              {/* History Link */}
              <div className='mt-4 flex justify-end'>
                <Button variant='outline' size='sm' onClick={() => onViewHistory(schedule)}>
                  <History className='h-4 w-4 mr-2' />
                  View Execution History
                </Button>
              </div>
            </div>
          </CollapsibleContent>
        </CardContent>
      </Collapsible>
    </Card>
  );
};

export default CompactScheduleCard;
